import { Suspense, lazy, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { HeartHandshake, LogOut } from "lucide-react";
import { api, currentRole, logout } from "../lib/api";
import { useFetch } from "../lib/useFetch";
// Pulls in the Stream SDK (~300 kB); keep it out of the main bundle.
const PortalMessages = lazy(() => import("../components/PortalMessages"));

interface Shift { id: number; startsAt: string; endsAt: string; serviceCategory: string; status: string }

/** Participants read this screen, so dates are written the way a person says
 *  them rather than the wire format. The machine-readable value stays in the
 *  <time> element's dateTime for anything parsing the page. */
const readableDateTime = (iso: string) =>
  new Date(iso).toLocaleString("en-AU", {
    weekday: "long", day: "numeric", month: "long", hour: "numeric", minute: "2-digit",
  });
const readableTime = (iso: string) =>
  new Date(iso).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" });
interface Goal { id: number; title: string; status: string }
interface Summary { participant: { firstName: string; lastName: string }; upcomingShifts: Shift[]; goals: Goal[] }

export default function Portal() {
  const navigate = useNavigate();
  const summary = useFetch<Summary>("/v1/portal/summary");
  const [fb, setFb] = useState({ category: "compliment", rating: "5", comment: "" });
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  if (currentRole() !== "participant") return <Navigate to="/" replace />;

  async function sendFeedback(e: React.FormEvent) {
    e.preventDefault();
    if (!fb.comment.trim()) return;
    try {
      await api("/v1/portal/feedback", { method: "POST", body: JSON.stringify({
        category: fb.category, rating: fb.rating ? Number(fb.rating) : undefined, comment: fb.comment.trim(),
      }) });
      setFb({ category: "compliment", rating: "5", comment: "" });
      setSendError(null);
      setSent(true); setTimeout(() => setSent(false), 8000);
    } catch (err) {
      setSendError((err as Error).message);
    }
  }

  return (
    <div className="min-h-screen bg-canvas">
      {/* Keyboard and switch users should not have to traverse the header to
          reach their supports. */}
      <a href="#portal-main" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-3 focus:rounded-lg focus:bg-teal-700 focus:px-4 focus:py-2 focus:text-white">
        Skip to main content
      </a>
      <header className="border-b border-edge bg-surface">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-teal-700 flex items-center justify-center"><HeartHandshake className="h-5 w-5 text-white" aria-hidden="true" /></div>
            <p className="font-semibold text-ink">My NDIS portal</p>
          </div>
          <button onClick={async () => { await logout(); navigate("/login"); }} className="flex items-center gap-2 text-sm text-mut hover:text-body">
            <LogOut className="h-4 w-4" aria-hidden="true" /> Sign out
          </button>
        </div>
      </header>

      <main id="portal-main" className="max-w-3xl mx-auto px-6 py-8 space-y-8">
        {summary.loading && (
          <p role="status" className="text-sm text-mut">Loading your portal…</p>
        )}
        {summary.error && (
          <div role="alert" className="card p-4">
            <h1 className="text-lg font-medium text-ink">We could not load your portal</h1>
            <p className="mt-1 text-sm text-mut">{summary.error}</p>
            <button className="btn-secondary mt-3" onClick={summary.reload}>Try again</button>
          </div>
        )}
        {summary.data && (
          <>
            <h1 className="text-2xl font-semibold text-ink">Welcome, {summary.data.participant.firstName}</h1>

            <section className="space-y-3">
              <h2 className="text-lg font-medium text-ink">Upcoming supports</h2>
              {summary.data.upcomingShifts.length === 0 && <p className="text-sm text-mut">No upcoming shifts scheduled.</p>}
              {summary.data.upcomingShifts.map((s) => (
                <div key={s.id} className="card p-4 flex items-center justify-between">
                  <span className="text-sm text-ink">
                    <time dateTime={s.startsAt}>{readableDateTime(s.startsAt)}</time>
                    {" until "}
                    <time dateTime={s.endsAt}>{readableTime(s.endsAt)}</time>
                  </span>
                  <span className="chip bg-teal-50 text-teal-700 capitalize">{s.serviceCategory.replace(/_/g, " ")}</span>
                </div>
              ))}
            </section>

            <section className="space-y-3">
              <h2 className="text-lg font-medium text-ink">My goals</h2>
              {summary.data.goals.map((g) => (
                <div key={g.id} className="card p-4 flex items-center justify-between">
                  <span className="text-sm text-ink">{g.title}</span>
                  <span className="chip bg-slate-100 text-body capitalize">{g.status}</span>
                </div>
              ))}
              {summary.data.goals.length === 0 && <p className="text-sm text-mut">No goals recorded yet.</p>}
            </section>

            <section className="space-y-3">
              <h2 className="text-lg font-medium text-ink">Messages</h2>
              <Suspense fallback={<p className="text-sm text-mut">Loading messages…</p>}>
                <PortalMessages />
              </Suspense>
            </section>

            <section className="space-y-3">
              <h2 className="text-lg font-medium text-ink">Share feedback</h2>
              <form onSubmit={sendFeedback} className="card p-5 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                <label className="block"><span className="label">Type</span>
                  <select value={fb.category} onChange={(e) => setFb({ ...fb, category: e.target.value })} className="input">
                    {["compliment", "suggestion", "concern", "other"].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select></label>
                <label className="block"><span className="label">Rating</span>
                  <select value={fb.rating} onChange={(e) => setFb({ ...fb, rating: e.target.value })} className="input">
                    <option value="">No rating</option>{[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select></label>
                <label className="block sm:col-span-2"><span className="label">Comment</span>
                  <input value={fb.comment} onChange={(e) => setFb({ ...fb, comment: e.target.value })} className="input" /></label>
                <div className="sm:col-span-4 flex items-center gap-3">
                  <button className="btn-dark justify-self-start">Send feedback</button>
                  {/* Announced, rather than only shown — the old version put the
                      confirmation inside the button's own label, where a screen
                      reader would not report it as a change. */}
                  <p role="status" className="text-sm text-teal-700">{sent ? "Thank you — your feedback has been sent." : ""}</p>
                </div>
                {sendError && <p role="alert" className="sm:col-span-4 text-sm text-red-700">{sendError}</p>}
              </form>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
